import { useEffect, useState } from 'react'

import { formatEther } from 'viem'

import { getChain, getGasToken } from '~/constants/chains'
import { GasEstimateResponse } from '~/hooks/transactions/useGasEstimates'
import { useMultipleTokensMetadata } from '~/hooks/useTokenMetadata'
import { useTokenPrices } from '~/hooks/useTokenPrices'
import { OrgRegularTransaction } from '~/types'
import { getTransactionGasCost } from '~/utils/smartAccounts'

/**
 * Calculates the transaction cost for both regular and merkle transactions
 * @param transaction - Either a merkle transaction with child transactions or a regular transaction with gas estimates
 * @returns Object containing transaction gas cost, USD cost, and loading state
 */
export const useTransactionCost = (
  transaction:
    | {
        isMerkleTransaction: true
        childTransactions: OrgRegularTransaction[]
      }
    | {
        isMerkleTransaction: false
        chainId: number
        gasEstimates?: GasEstimateResponse['gasEstimates']
        transactionHash?: string
      },
): {
  /** Total gas cost in wei, undefined if merkle transaction */
  transactionGas: bigint | undefined
  /** Total gas cost in USD */
  transactionCost: number | undefined
  /** Gas cost grouped by chain ID in USD */
  transactionCostByChain: Record<number, number> | undefined
  /** Whether the cost calculation is currently loading */
  isLoading: boolean
} => {
  const merkleTransaction = transaction.isMerkleTransaction
    ? transaction
    : undefined
  const regularTransaction = transaction.isMerkleTransaction
    ? undefined
    : transaction
  const [gasList, setGasList] = useState<bigint[]>()
  const tokens = useMultipleTokensMetadata(
    transaction.isMerkleTransaction
      ? transaction.childTransactions.map((childTransaction) => {
          return {
            address: getGasToken(childTransaction.chainId),
            chainId: childTransaction.chainId,
          }
        })
      : [
          {
            address: getGasToken(transaction.chainId),
            chainId: transaction.chainId,
          },
        ],
  )
  const { getPrice } = useTokenPrices()
  const [isLoading, setIsLoading] = useState(false)
  useEffect(() => {
    async function fetchMerkleCost() {
      if (!merkleTransaction?.childTransactions) return
      setIsLoading(true)

      try {
        // A child on a chain the registry no longer lists (e.g. history from a
        // delisted chain) has no RPC to price against — count it as 0 rather
        // than reject and leave the cost spinner stuck.
        const costs = await Promise.all(
          merkleTransaction.childTransactions.map((childTransaction) =>
            getChain(childTransaction.chainId)
              ? getTransactionGasCost(childTransaction)
              : Promise.resolve(0n),
          ),
        )
        setGasList(costs)
      } finally {
        setIsLoading(false)
      }
    }

    fetchMerkleCost()
  }, [merkleTransaction?.childTransactions])

  useEffect(() => {
    async function fetchCost() {
      if (
        !regularTransaction?.chainId ||
        !regularTransaction.gasEstimates?.callGasLimit ||
        !getChain(regularTransaction.chainId)
      )
        return

      setIsLoading(true)

      try {
        const cost = await getTransactionGasCost({
          chainId: regularTransaction.chainId,
          callGasLimit: regularTransaction.gasEstimates.callGasLimit,
          verificationGasLimit:
            regularTransaction.gasEstimates.verificationGasLimit,
          preVerificationGas:
            regularTransaction.gasEstimates.preVerificationGas,
          maxFeePerGas: regularTransaction.gasEstimates.maxFeePerGas,
          maxPriorityFeePerGas:
            regularTransaction.gasEstimates.maxPriorityFeePerGas,
          transactionHash: regularTransaction.transactionHash ?? null,
        })
        setGasList([cost])
      } finally {
        setIsLoading(false)
      }
    }
    fetchCost()
  }, [
    regularTransaction?.chainId,
    regularTransaction?.gasEstimates?.callGasLimit,
    regularTransaction?.gasEstimates?.verificationGasLimit,
    regularTransaction?.gasEstimates?.preVerificationGas,
    regularTransaction?.gasEstimates?.maxFeePerGas,
    regularTransaction?.gasEstimates?.maxPriorityFeePerGas,
    regularTransaction?.transactionHash,
  ])

  const transactionCostByChain =
    tokens && gasList
      ? tokens.reduce<Record<number, number>>((acc, token, index) => {
          const gas = gasList[index]
          const price = getPrice(token.chainId, token.address)

          if (!price) return acc

          const gasCostUsd = Number(formatEther(gas)) * price

          acc[token.chainId] = (acc[token.chainId] ?? 0) + gasCostUsd
          return acc
        }, {})
      : undefined

  const transactionCost = transactionCostByChain
    ? Object.values(transactionCostByChain).reduce((sum, cost) => sum + cost, 0)
    : undefined

  const transactionGas =
    tokens && gasList && !transaction.isMerkleTransaction
      ? tokens.reduce((acc, _, index) => {
          const gas = gasList[index]
          return acc + gas
        }, 0n)
      : undefined

  return {
    transactionGas,
    transactionCost,
    transactionCostByChain,
    isLoading,
  }
}
